import {useAuthInfo} from "@propelauth/react";
import {Card, CardContent, CardHeader, CardTitle,} from "../../ui/card";
import {fetchWithToken, postWithToken} from "../../../api/apiBase";
import {CompanyType} from "../../../models/company";
import React, {useContext, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import {Button} from "../../ui/button";
import BaseLoadingSpinner from "../../ui/baseLoadingSpinner";
import {BaseScopeModel, blankScope, DeveloperScopeCardModel, FullScopeModel} from "../ScopeModel";
import {DeveloperScopeCard} from "../developer/ScopeListItem";
import {ScopeForm} from "../ScopeForm";
import {useToast} from "../../ui/use-toast";
import {ProjectContext} from "../../../contexts/ProjectContext";
import {
    DndContext,
    DragEndEvent,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

type ScopePageProps = {
    creating: boolean
}

function SortableScopeCard({scope}: { scope: DeveloperScopeCardModel }) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
        useSortable({id: scope.id});

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        // Optional: give a hint the whole card is draggable
        cursor: "grab",
        // Optional: prevent layout jump
        willChange: "transform"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <DeveloperScopeCard scope={scope}/>
        </div>
    );
}



/**
 *
 * todo - add the newly created data to the list on submit.
 * done
 *
 * */
export default function DeveloperScopesListPage() {
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<string>();
    const projectContext = useContext(ProjectContext);
    const {toast} = useToast();
    const navigate = useNavigate();
    const [isCreatingNew, setisCreatingNew] = useState<boolean>(false);
    const [editingQA, setEditingQA] = useState<BaseScopeModel | null>(null);
    const [data, setData] = useState<DeveloperScopeCardModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (!accessToken) {
            return
        }
        try {
            setIsLoading(true);
            const response = await fetchWithToken(`/projects/${project_id}/scopes`, accessToken);
            response.sort((a:DeveloperScopeCardModel, b:DeveloperScopeCardModel) => a.number - b.number);
            setData(response);
            console.log(response);
            setError(null);
        } catch (err) {
            setError("Error loading QA :(");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData()
    }, [project_id, accessToken]);



    const onCancelNew = () => {
        setisCreatingNew(false);
    };

    const handleEdit = (qaItem: BaseScopeModel) => {
        setEditingQA(qaItem);
    };

    const handleCancelEdit = () => {
        setEditingQA(null);
    };

    const handleSaveEdit = (newScope: BaseScopeModel) => {
        console.log(newScope);
        postWithToken(
            `/projects/${project_id}/scope`,
            JSON.stringify(newScope), accessToken as string)
            .then((new_data) => {
                setData([...data, new_data]);
                toast({
                    title: "Success!",
                    description: "Scope created successfully",
                    variant: "default",
                });
            }).catch((error) => {
            toast({
                title: "Error",
                description: error.message || "Failed to Create Scope.",
                variant: "destructive",
            });

        }).finally(() => {
            setEditingQA(null);
            setisCreatingNew(false);
        })
    };

    const handleSaveOrder = async (ids: string[]) => {
        postWithToken(`/projects/${project_id}/scopes/re_order`,
            JSON.stringify({ids: ids}),
                accessToken as string)
            .then((data) => {
                data.sort((a:DeveloperScopeCardModel, b:DeveloperScopeCardModel) => a.number - b.number)
                setData(data);
            })
            .catch((error) => {
                toast({
                title: "Error",
                description: error.message || "Failed to update Scopes.",
                variant: "destructive",
            });
            })
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
        useSensor(KeyboardSensor) // enables keyboard drag
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;

        const oldIndex = data.findIndex((x) => x.id === active.id);
        const newIndex = data.findIndex((x) => x.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const next = arrayMove(data, oldIndex, newIndex);
        console.log(data, oldIndex, newIndex);
        setData(next);
        await handleSaveOrder(next.map(item => item.id));
        // TODO: persist new order to your backend here
        // await api.saveOrder(next.map(item => item.id));
    };

    if (projectContext?.role !== CompanyType.developer) return <></>;
    if (isLoading) return <div><BaseLoadingSpinner/></div>;
    if (error)
        return (
            <div>Error loading Scopes :(</div>
        );
    return (
        <div className="space-y-6">
            <CardHeader>
                <CardTitle>
                    <div className="flex justify-between items-center">
                        <h1 className="text-xm font-semibold">
                            Scopes
                        </h1>
                        {projectContext?.role === CompanyType.developer && (
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/project/${project_id}/scopes`)}
                                >
                                    Bid Tab
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setisCreatingNew(true)}
                                >
                                    Add Scope
                                </Button>
                            </div>
                        )}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isCreatingNew && (
                    <div>
                        <Card>
                            <ScopeForm
                                editable={true}
                                onSave={handleSaveEdit}
                                onSubmit={handleSaveEdit}
                                scope={blankScope() as FullScopeModel}
                                canInvite={false}
                                create={false}
                                editing={true}
                                onCancel={onCancelNew}
                            />
                        </Card>
                    </div>
                )}
                {data.length === 0 && !isCreatingNew ?
                    "No Scopes to show"
                    :<DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        {/* SortableContext provides list semantics; pass stable ids order */}
                        <SortableContext
                            items={data.map((x) => x.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {data.map((qa) => (
                                <SortableScopeCard key={qa.id} scope={qa}/>
                            ))}
                        </SortableContext>
                    </DndContext>
                    // : <>{data.map((sc: DeveloperScopeCardModel, index) =>
                    //     <DeveloperScopeCard key={sc.id} scope={sc}/>)}</>
                }
            </CardContent>
        </div>
    );
}


